// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type ApiError } from './client.js';

/** Scopes an admin can grant here; mirrors the api's GRANTABLE_SCOPES. */
export type ApiKeyScope = 'forms:read' | 'read';

/** API-safe view — never carries the token or its hash. */
export interface ApiKeyView {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** POST response — `token` (plaintext) is present exactly once. */
export interface CreatedApiKey extends ApiKeyView {
  token: string;
}

const QUERY_KEY = ['api-keys'] as const;

export function useApiKeys() {
  return useQuery<ApiKeyView[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await apiFetch<{ items: ApiKeyView[] }>('/api/api-keys');
      return res.items;
    },
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation<CreatedApiKey, ApiError, { name: string; scopes: ApiKeyScope[] }>({
    mutationFn: (body) => apiFetch<CreatedApiKey>('/api/api-keys', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (id) => apiFetch(`/api/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
